/**
 * The one gate between the keyboard and session content while it is not editable
 * (`canEditSessionContent` false: loading, or a session switch's hydrate). `inert` stops pointer
 * and focus, but the workspace's shortcuts listen on the window, where `inert` reaches nothing, so
 * an edit made there (delete, paste, undo, a canvas or timeline shortcut) would land on session
 * content the incoming hydrate then overwrites.
 *
 * Installed before React renders, so its capture listener on the window runs ahead of every
 * shortcut's, capture or bubble, whatever mounts first. While locked it stops every keydown, wherever
 * focus is: the shortcuts act on session content from anywhere, a focused session-panel button or a
 * dialog button included. A text field passes (a key there is typing), and Tab from inside the
 * replay recovery dialog passes (it moves focus; it is never a shortcut). It never prevents the
 * browser's own default, so reload, close and find still work, and Enter or Space still activates
 * the focused button. Keyup passes, so no held-key state sticks.
 */

let locked = false;

const TEXT_INPUT_TYPES = new Set(['text', 'search', 'email', 'url', 'tel', 'password', 'number']);

function isTextField(element) {
  if (element.isContentEditable) return true;
  if (element.tagName === 'TEXTAREA') return true;
  return element.tagName === 'INPUT' && TEXT_INPUT_TYPES.has((element.type || 'text').toLowerCase());
}

/** Whether `event` must not reach a shortcut while session content is locked. */
export function isSessionContentKeyEvent(event) {
  const target = event?.target;
  if (!target || typeof target.closest !== 'function') return true;
  if (isTextField(target)) return false;
  return !(event.key === 'Tab' && target.closest('[data-replay-recovery-dialog="true"]'));
}

/** Set by the workspace from `canEditSessionContent` (`useSessionContentKeyLock`). */
export function setSessionContentKeysLocked(next) {
  locked = Boolean(next);
}

export function installSessionContentKeyGate(win = window) {
  if (!win?.addEventListener) return () => {};
  const gate = (event) => {
    if (!locked || !isSessionContentKeyEvent(event)) return;
    event.stopImmediatePropagation();
  };
  win.addEventListener('keydown', gate, true);
  return () => win.removeEventListener('keydown', gate, true);
}
